export type ExtraBasis = "flat" | "per_person";

export interface BookingExtraDraft {
  description: string;
  amount: number;
  basis: ExtraBasis;
  quantity: number;
  notify_supplier?: boolean;
  include_in_commission?: boolean;
}

/** Line total: flat = amount once; per person = amount × number of people. */
export function extraLineTotal(e: Pick<BookingExtraDraft, "amount" | "basis" | "quantity">): number {
  const amount = Number(e.amount) || 0;
  const qty = e.basis === "per_person" ? Math.max(0, Number(e.quantity) || 0) : 1;
  return Math.round(amount * qty * 100) / 100;
}

export function extrasTotal(extras: Array<Pick<BookingExtraDraft, "amount" | "basis" | "quantity"> & { status?: string }>): number {
  return Math.round(
    extras.filter((e) => (e.status ?? "active") === "active").reduce((s, e) => s + extraLineTotal(e), 0) * 100
  ) / 100;
}

/** Split extras into commissionable vs not (active only). */
export function splitExtras(extras: Array<BookingExtraDraft & { status?: string }>) {
  const active = extras.filter((e) => (e.status ?? "active") === "active");
  return {
    commissionable: extrasTotal(active.filter((e) => e.include_in_commission)),
    nonCommissionable: extrasTotal(active.filter((e) => !e.include_in_commission)),
  };
}

/** Commission is paid on the booking total minus non-commissionable extras. */
export function commissionBase(totalAmount: number, nonCommissionableExtras: number): number {
  return Math.max(0, Math.round(((Number(totalAmount) || 0) - (Number(nonCommissionableExtras) || 0)) * 100) / 100);
}

export function validExtras<T extends BookingExtraDraft>(extras: T[]): T[] {
  return extras.filter((e) => e.description.trim() && extraLineTotal(e) > 0);
}

/**
 * Booking total: commission (agents invoiced net) applies to tours + commissionable
 * extras; non-commissionable extras are added in full afterwards.
 */
export function computeBookingTotal({ toursSubtotal, commissionRate, extras, commissionAlreadyDeducted }: {
  toursSubtotal: number; commissionRate: number; extras: Array<BookingExtraDraft & { status?: string }>;
  commissionAlreadyDeducted?: boolean;
}) {
  const s = splitExtras(extras);
  const commissionable = (Number(toursSubtotal) || 0) + s.commissionable;
  const rate = commissionAlreadyDeducted ? 0 : Number(commissionRate) || 0;
  const commission = Math.round(commissionable * (rate / 100) * 100) / 100;
  const total = Math.round((commissionable - commission + s.nonCommissionable) * 100) / 100;
  return { commission, total, ...s };
}

export interface CommissionLine { subtotal: number; commissionOverride?: number | null }

/** Rate for one tour line: its override if set (0 allowed), else the agent default. */
export function lineCommissionRate(defaultRate: number, override?: number | null): number {
  return override != null && Number.isFinite(Number(override)) ? Number(override) : Number(defaultRate) || 0;
}

/**
 * Extra commission from per-tour overrides relative to the default rate
 * (negative when an override is lower). Add to default-rate commission.
 */
export function overrideCommissionDelta(defaultRate: number, lines: CommissionLine[]): number {
  const d = lines.reduce((s, l) => s + (Number(l.subtotal) || 0) * (lineCommissionRate(defaultRate, l.commissionOverride) - (Number(defaultRate) || 0)) / 100, 0);
  return Math.round(d * 100) / 100;
}

/** Agent invoice total with per-tour commission overrides. */
export function computeLinesTotal({ lines, defaultRate, extras }: {
  lines: CommissionLine[]; defaultRate: number; extras: Array<BookingExtraDraft & { status?: string }>;
}) {
  const toursSubtotal = lines.reduce((s, l) => s + (Number(l.subtotal) || 0), 0);
  const base = computeBookingTotal({ toursSubtotal, commissionRate: defaultRate, extras });
  const delta = overrideCommissionDelta(defaultRate, lines);
  const commission = Math.round((base.commission + delta) * 100) / 100;
  return { ...base, toursSubtotal, commission, total: Math.round((base.total - delta) * 100) / 100 };
}
